import React, { useContext, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { UserContext } from "@/store/context/UserContext.tsx";
import { useTranslation } from "react-i18next";
import { constants } from "@/constants";
import Api from "@/store/api.ts";
import Logger from "@/utils/logger";
import { EventBus, EventBusEvents } from "@/store/event-bus.ts";
import { isPlatform, platformWizyChat } from "@/domains.ts";

const Paddle: any = (window as any).Paddle;
const Rewardful: any = (window as any).Rewardful;

export const ProfilePageWizy: React.FC = () => {
  const { userSettings, invalidateSettings } = useContext(UserContext);
  const { t } = useTranslation();

  // Billing handlers --------------------

  const onClickSubscription = (productID: null, coupon: string) => {
    const referral = Rewardful && Rewardful.referral;
    Paddle.Setup({ vendor: constants.paddle.vendorID });

    let passthrough = {
      user_id: userSettings.userID,
      source: constants.paddle.sourceName,
      rewardful: { referral: referral },
    };

    Paddle.Checkout.open({
      product: productID,
      passthrough: JSON.stringify(passthrough),
      disableLogout: false,
      coupon: coupon,
      successCallback: () => {
        setTimeout(() => {
          Api.dispatchSettingsEvent();
          invalidateSettings();
        }, 3000);
      },
      closeCallback: "checkoutClosed",
    });
  };

  const checkRedirectEvent = () => {
    let lsString = window.localStorage.getItem("redirect-command");
    if (!lsString) {
      return;
    }
    window.localStorage.removeItem("redirect-command");

    let eventObj: any | null = {};
    try {
      eventObj = JSON.parse(lsString);
    } catch (e) {
      Logger.error(e);
    }

    if (!eventObj.cmd || eventObj.cmd !== "subscribe") {
      return;
    }
    let plan: any | null = constants.pricing.filter((item) => {
      return item.name === eventObj.plan && item.yearly === eventObj.yearly;
    });
    plan = plan.length ? plan[0] : null;
    if (plan) {
      onClickSubscription(
        plan.productID,
        eventObj.promocode ? eventObj.promocode : undefined,
      );
    }
  };

  useEffect(checkRedirectEvent, []);
  EventBus.getInstance().register(
    EventBusEvents.settingsUpdated,
    checkRedirectEvent,
  );

  const isSubscriptionActive =
    userSettings.subscription && userSettings.subscription.active;

  let productName = "Free";
  if (isSubscriptionActive) {
    productName = userSettings.subscription.product_name;
  }

  return (
    <div className="grid grid-cols-1 gap-3">
      <Card className="">
        <CardHeader className="relative">
          <CardTitle>{t("user")}</CardTitle>
          <CardDescription></CardDescription>
        </CardHeader>
        <CardContent>
          <p className="py-2">
            Email:&nbsp;
            <span className="font-semibold">{userSettings.email}</span>
          </p>
          {/*<p className="py-2">*/}
          {/*  {t("user_id")}:&nbsp;*/}
          {/*  {userSettings.userID}*/}
          {/*</p>*/}
        </CardContent>
        <CardFooter className="flex justify-between"></CardFooter>
      </Card>

      <Card className="">
        <CardHeader className="relative">
          <CardTitle>{t("balance")}</CardTitle>
          <CardDescription></CardDescription>
        </CardHeader>
        <CardContent>
          <p className="py-2">
            {t("billing.current_plan")}:&nbsp;
            <span className="font-semibold">{productName}</span>
          </p>

          <p className="py-2">
            {t("billing.message_credits")}:&nbsp;
            <span className="font-semibold">{userSettings.messageCredits}</span>
          </p>

          {isPlatform(platformWizyChat) && productName == "Free" && (
            <a
              type="button"
              className="btn btn inline-flex justify-center btn-dark min-w-[165px] mt-4"
              href="/pricing"
            >
              <span className="flex items-center cursor-pointer">
                Upgrade Plan
              </span>
            </a>
          )}
        </CardContent>
        <CardFooter className="flex justify-between"></CardFooter>
      </Card>

      {userSettings.userID && isSubscriptionActive && (
        <Card className="">
          <CardHeader className="relative">
            <CardTitle>{t("subscription")}</CardTitle>
            <CardDescription></CardDescription>
          </CardHeader>
          <CardContent>
            <p className="py-2">
              {t("billing.subscription_status")}:&nbsp;
              <span className="font-semibold">
                {userSettings.subscription.active ? "Active" : "Canceled"}
              </span>
            </p>

            {userSettings.subscription.unit_price && (
              <p className="py-2">
                {t("billing.subscription_price")}:&nbsp;
                <span className="font-semibold">
                  ${userSettings.subscription.unit_price} /mo
                </span>
              </p>
            )}

            {userSettings.subscription.next_bill_date && (
              <p className="py-2">
                {t("billing.next_bill_date")}:&nbsp;
                <span className="font-semibold">
                  {userSettings.subscription.next_bill_date}
                </span>
              </p>
            )}

            <div className="mt-5 space-xy-5">
              {userSettings.subscription.cancel_url && (
                <a
                  className="btn btn inline-flex justify-center btn-outline-dark"
                  href={userSettings.subscription.cancel_url}
                  target="_blank"
                >
                  <span className="flex items-center">Cancel Subscription</span>
                </a>
              )}
              {userSettings.subscription.update_url && (
                <a
                  className="btn btn inline-flex justify-center btn-outline-dark"
                  href={userSettings.subscription.update_url}
                  target="_blank"
                >
                  <span className="flex items-center">Update Billing Info</span>
                </a>
              )}
              {userSettings.subscription.receipt_url && (
                <a
                  className="btn btn inline-flex justify-center btn-outline-dark"
                  href={userSettings.subscription.receipt_url}
                  target="_blank"
                >
                  <span className="flex items-center">Receipt</span>
                </a>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between"></CardFooter>
        </Card>
      )}
    </div>
  );
};
